/* eslint max-len: ["error", 200] */

import React from 'react';
import qs from 'qs';

import { useParams } from 'react-router';
import { Route } from '@reverbdotcom/commons/src/route_settings';
import { MParticlePageName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import MarketplaceRoute from './routes/marketplace';
import { BrandRoute } from './routes/brand';
import CSPIndexRoute from './routes/csp_index';
import SaleRoute from './routes/sale';
import {
  ItemShowRoute,
  ItemPreviewRoute,
  ItemPreviewConfirmRoute,
} from './routes/item_show';
import HandpickedRoute from './routes/handpicked';
import CollectionRoute from './routes/collection';
import { EndedWatchedProductsRoute, PublicEndedWatchedProductsRoute } from './routes/ended_watched_products';
import RecentlyViewedRoute from './routes/recently_viewed';
import { WatchedProductsRoute, PublicWatchedProductsRoute } from './routes/watched_products';
import FeedCustomizationRoute from './routes/feed_customization';
import HomepageRoute from './routes/homepage';
import OnboardingRoutes from './routes/progressive_onboarding';
import { CmsRoutes } from './routes/cms_route';
import {
  CoreIdentityVerificationRoute,
} from './routes/identity_verification';
import HolidaySaleRoute from './routes/holiday_sale';
import { ShopRoutes } from './routes/shop';
import { PRICE_GUIDE_ROUTE } from './components/shared/url_helpers';
import { CheckoutAnalyticsRoutes, CheckoutRoutes } from './routes/checkout';
import TrackArticle from '@reverbdotcom/commons/src/components/track_article_page_view';
import {
  MyFavoritesLandingRoute,
  MyFavoritesWatchListRoute,
  MyFavoritesWatchListEndedRoute,
  MyFavoritesSearchesRoute,
  MyFavoritesShopsRoute,
  MyFavoritesFeedRoute,
  MyFavoritesDiscoverRoute,
  MySavedLandingRoute,
  MySavedWatchListRoute,
  MySavedWatchListEndedRoute,
  MySavedSearchesRoute,
  MySavedShopsRoute,
  MySavedFeedRoute,
  MySavedDiscoverRoute,
} from './routes/my_favorites';
import { BrowsePageRoutes } from './routes/browse_page_routes';

/*
 * All routes for the ReverbRouter are defined here. These are exported
 * separately so they can be shared between the client and server side
 * Router.
 *
 * Note: All routes here are defined with a relative route to support international
 * prefix routing, which is defined below in the actual export
 *
 * Please add a key to allow React to do some fancy DOM diffing (and to prevent an error in console)
 */

// v7 router state locations carry `search` (a string), not the parsed
// v3-style `query` object. `search` is optional on the Partial<Location> that
// pageViewEvent builders receive.
function queryFrom(location: { search?: string }) {
  return qs.parse(location.search ?? '', { ignoreQueryPrefix: true });
}

// TrackArticle's graphql config reads props.params.slug (injected by the v3
// router; its own conversion happens in the withRouter sweep).
function TrackArticlePage() {
  const params = useParams();
  const routeProps = { params } as any;

  return <TrackArticle {...routeProps} />;
}

const Routes = [
  <Route
    key="admin/_frontend_error"
    path="admin/_frontend_error"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'marketplace' */
        './components/frontend_error'
      )).default,
    })}
  />,
  <Route
    key="my/selling/bump-listing/:listingId"
    path="my/selling/bump-listing/:listingId"
    handle={{
      clientSide: false,
      pageViewEvent: {
        pageName: MParticlePageName.MobileBump,
      },
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'mobile-bump-listing' */
        './components/bump/mobile_bump_container'
      )).default,
    })}
  />,
  <Route
    key="my/selling/direct-offer/:listingId"
    path="my/selling/direct-offer/:listingId"
    handle={{
      clientSide: false,
      pageViewEvent: {
        pageName: MParticlePageName.MobileDirectOffer,
      },
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'mobile-direct-offer' */
        './components/listings/mobile_direct_offer_container'
      )).default,
    })}
  />,
  <Route
    key="my/selling/advertising"
    path="my/selling/advertising"
    handle={{
      clientSide: false,
      pageViewEvent: {
        pageName: MParticlePageName.SellerDashboard,
      },
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'seller-advertising-dashboard' */
        './components/seller_dashboard/seller_dashboard_container'
      )).default,
    })}
  />,
  <Route
    key="my/selling/trade_ins"
    path="my/selling/trade_ins"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/selling/trade_ins/trade_ins_manager'
      )).default,
    })}
  />,
  <Route
    key="my/selling/trade_ins/:tradeInId"
    path="my/selling/trade_ins/:tradeInId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/selling/trade_ins/trade_in_show'
      )).default,
    })}
  />,
  <Route
    key="my/collection"
    path="my/collection"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/index/GearCollectionIndexLayout'
      )).default,
    })}
  />,
  <Route
    key="my/collection/new"
    path="my/collection/new"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/new/GearCollectionItemNewLayout'
      )).default,
    })}
  />,
  <Route
    key="my/collection/search"
    path="my/collection/search"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/csp_search/GearCollectionCspSearch'
      )).default,
    })}
  />,
  <Route
    key="my/collection/lists/:listId"
    path="my/collection/lists/:listId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/gear_collection_groups/show/Show'
      )).default,
    })}
  />,
  <Route
    key="my/collection/lists"
    path="my/collection/lists"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/gear_collection_groups/index/GearCollectionGroupsIndex'
      )).default,
    })}
  />,
  <Route
    key="my/collection/:gearCollectionItemId"
    path="my/collection/:gearCollectionItemId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/show/GearCollectionItemShowLayout'
      )).default,
    })}
  />,
  <Route
    key="my/collection/:gearCollectionItemId/edit"
    path="my/collection/:gearCollectionItemId/edit"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collections' */
        './components/gear_collections/edit/GearCollectionItemEditLayout'
      )).default,
    })}
  />,
  <Route
    key="collection/:profileSlug/lists/:listId"
    path="collection/:profileSlug/lists/:listId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collection_groups' */
        './components/gear_collections/public/gear_collection_groups/Show'
      )).default,
    })}
  />,
  <Route
    key="collection/:profileSlug/items/:itemId"
    path="collection/:profileSlug/items/:itemId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'gear_collection_items' */
        './components/gear_collections/public/gear_collection_items/Show'
      )).default,
    })}
  />,
  <Route
    key="trade-in"
    path="trade-in"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'express_sale' */
        './components/express_sale/index/express_sale_index'
      )).default,
    })}
  />,
  <Route
    key="checkout/:checkoutId/trade-in"
    path="checkout/:checkoutId/trade-in"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'express_sale' */
        './components/express_sale/checkout/express_sale_checkout'
      )).default,
    })}
  />,
  <Route
    key="feed"
    path="my/feed"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'feed' */
        './feed/feed_grid'
      )).default,
    })}
  />,
  <Route
    key="my/recommendations"
    path="my/recommendations"
    handle={{
      clientSide: true,
      pageViewEvent: {
        pageName: MParticlePageName.RecommendedListings,
      },
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'recommendations' */
        './components/recommended_listings/recommended_listings_container'
      )).default,
    })}
  />,
  <Route
    key="sell"
    path="sell"
    handle={{
      pageViewEvent: ({ location }) => ({
        pageName: MParticlePageName.SellForm,
        promotionalToken: queryFrom(location).promotional_token as string,
      }),
    }}
  />,
  <Route
    key="sell/search"
    path="sell/search"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './components/sell/sell_search'
      )).default,
    })}
  />,
  <Route
    key="my/selling/listings/:listingId/edit"
    path="my/selling/listings/:listingId/edit"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './components/sell/form/SellFormEditListingLayout'
      )).default,
    })}
  />,
  <Route
    key="my/selling/listings/new"
    path="my/selling/listings/new"
    handle={{
      clientSide: true,
      pageViewEvent: ({ location }) => ({
        pageName: MParticlePageName.SellForm,
        cspId: queryFrom(location).seed_id as string,
        promotionalToken: queryFrom(location).promotional_token as string,
      }),
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './components/sell/form/SellFormNewListingLayout'
      )).default,
    })}
  />,
  <Route
    key="my/selling/listings/:listingId/payment_settings"
    path="my/selling/listings/:listingId/payment_settings"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './dashboard/selling/listings_manager/PaymentSettings'
      )).default,
    })}
  />,
  <Route
    key="my/selling/listings/:listingId/cross_platform_start/v1"
    path="my/selling/listings/:listingId/cross_platform_start/v1"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './components/sell/CrossPlatformMobilePhotoUploaderLayout'
      )).default,
    })}
  />,
  <Route
    key="sell-form-setup"
    path="sell-form-setup"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'sell' */
        './components/sell/SellFormSetupPage'
      )).default,
    })}
  />,
  <Route
    key="seller_verification/onboarding"
    path="seller_verification/onboarding"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        './onboarding/seller_verification/shop_setup_wrapper'
      )).default,
    })}
  />,
  <Route
    key="item/:slug/similar"
    path="item/:slug/similar"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'similar-listings' */
        './components/discovery/similar_listings_grid'
      )).default,
    })}
  />,
  <Route
    key="similar/:id"
    path="similar/:id"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'similar-listings' */
        './components/discovery/similar_listings_grid'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shipping_labels/new/:orderId"
    path="my/selling/shipping_labels/new/:orderId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shipping-labels' */
        './components/discovery/shipping_labels/shipping_labels_form_container'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shipping_labels/:shipmentId"
    path="my/selling/shipping_labels/:shipmentId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shipping-labels' */
        './components/discovery/shipping_labels/shipping_rates_form'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shipping_labels/:shipmentId/insurance"
    path="my/selling/shipping_labels/:shipmentId/insurance"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shipping-labels' */
        './components/discovery/shipping_labels/shipping_insurance_form_container'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shipping_labels/:shipmentId/edit"
    path="my/selling/shipping_labels/:shipmentId/edit"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shipping-labels' */
        './components/discovery/shipping_labels/edit_shipment_container'
      )).default,
    })}
  />,
  <Route
    key="price-guide"
    path={PRICE_GUIDE_ROUTE}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'price-guide' */
        './components/price_guides/price_guide_page'
      )).default,
    })}
  />,
  <Route
    key="p/:slug/"
    handle={{
      clientSide: true,
    }}
    path="p/:slug/:condition?"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'csp' */
        './components/csp/csp_layout'
      )).default,
    })}
  />,
  <Route
    key="page/:slug"
    path="page/:slug"
    handle={{
      pageViewEvent: (routerState) => ({
        pageName: MParticlePageName.Page,
        pageSlug: routerState.matches[routerState.matches.length - 1]?.params.slug,
      }),
    }}
  />,
  <Route
    key="news"
    path="news"
    handle={{
      pageViewEvent: ({ location }) => ({
        pageName: MParticlePageName.NewsHomepage,
        articleQuery: queryFrom(location).query,
      }),
    }}
  />,
  // /news/the-forgotten-guitars-of-jimi-hendrix/locale/ja
  // We have the optional /locale portion here because we
  // have a number of links in the wild that use this
  // routing patern.
  //
  // Our Rails router handles this case without a 404,
  // and renders the content in the specified "locale"
  //
  // This ensures that the React Router is at parity
  // https://sentry.io/organizations/reverb-llc/issues/1770671512/?project=1769470
  <Route
    key="news/:slug"
    path="news/:slug/locale?/:locale?"
    Component={TrackArticlePage}
  />,
  <Route
    key="my/selling/packlink_shipments/:orderId"
    path="my/selling/packlink_shipments/:orderId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'mobile-packlink-layout' */
        './components/packlink_mobile_layout'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shipping_profiles/new"
    path="my/selling/shipping_profiles/new"
    handle={{
      pageViewEvent: {
        pageName: MParticlePageName.CreateShippingProfileForm,
      },
    }}
  />,
  <Route
    key="my/selling/shop_campaign"
    path="my/selling/shop_campaign"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shop_campaigns' */
        './components/selling/shop_campaigns/ShopCampaignSellerIndexLayout'
      )).default,
    })}
  />,
  <Route
    key="my/selling/shop_campaign/new"
    path="my/selling/shop_campaign/new"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'shop_campaigns' */
        './components/selling/shop_campaigns/ShopCampaignSellerNewLayout'
      )).default,
    })}
  />,
  <Route
    key="top/:slug/:child"
    path="top/:slug/:child?"
    handle={{ clientSide: true }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'category_top_products' */
        './components/category_top_products'
      )).default,
    })}
  />,
  <Route
    key="software(/:categorySlug)"
    path="software/:categorySlug?"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'software' */
        './digital_listings/index_page'
      )).default,
    })}
  />,
  <Route
    key="software/:categorySlug/:brandSlug/:digitalListingId"
    path="software/:categorySlug/:brandSlug/:digitalListingId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'software' */
        './digital_listings/show_page'
      )).default,
    })}
  />,
  <Route
    key="my/selling/orders"
    path="my/selling/orders"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/selling/orders/selling_orders_manager'
      )).default,
    })}
  />,
  <Route
    key="my/buying/orders"
    path="my/buying/orders"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/buying/orders/buying_orders_manager'
      )).default,
    })}
  />,
  <Route
    key="my/selling/listings"
    path="my/selling/listings"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/selling/listings_manager/ListingsManager'
      )).default,
    })}
  />,
  <Route
    key="my/account"
    path="my/account"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/seller_hub/SellerHub'
      )).default,
    })}
  />,
  <Route
    key="redeem"
    path="redeem" // Rails view, just used for pageViewEvent
    handle={{
      clientSide: false,
      pageViewEvent: (routerState) => ({
        pageName: MParticlePageName.RedeemGiftCard,
        prefilled: !!queryFrom(routerState.location).token,
      }),
    }}
  />,
  <Route
    key="f/:brand/:product_family"
    path="f/:brand/:product_family"
    handle={{ clientSide: false }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'product-family' */
        './components/product_family/product_family_page'
      )).default,
    })}
  />,
  <Route
    key="outlet"
    path="outlet"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'outlet' */
        './components/outlet/outlet_layout'
      )).default,
    })}
  />,
  <Route
    key="my/buying/trade_ins"
    path="my/buying/trade_ins"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/buying/trade_ins/trade_ins_manager'
      )).default,
    })}
  />,
  <Route
    key="my/buying/trade_ins/:tradeInId"
    path="my/buying/trade_ins/:tradeInId"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'dashboard' */
        './dashboard/buying/trade_ins/trade_in_show'
      )).default,
    })}
  />,
  <Route
    key="sell/listing-options"
    path="sell/listing-options"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'listing-options' */
        './components/sell/SellOptionsSelector'
      )).default,
    })}
  />,
  <Route
    key="sell/trade-in"
    path="sell/trade-in"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'seller-trade-in' */
        './components/sell/SellerTradeIn'
      )).default,
    })}
  />,
  <Route
    key="artist-shops"
    path="artist-shops"
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'artist-shops' */
        './components/artist_shops/artist_shop_landing_page'
      )).default,
    })}
  />,
  <Route
    key="billing_method/onboarding"
    path="billing_method/onboarding"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        './onboarding/billing_method_page'
      )).default,
    })}
  />,
  <Route
    key="sell/confirm-condition"
    path="sell/confirm-condition"
    handle={{
      clientSide: true,
    }}
    lazy={async () => ({
      Component: (await import(
        /* webpackChunkName: 'confirm-condition' */
        './components/sell/ConditionConfirmationForm'
      )).default,
    })}
  />,
];

Routes.push(
  ShopRoutes,
  CSPIndexRoute,
  HomepageRoute,
  SaleRoute,
  ItemShowRoute,
  ItemPreviewRoute,
  ItemPreviewConfirmRoute,
  HandpickedRoute,
  BrandRoute,
  CollectionRoute,
  ...MarketplaceRoute,
  EndedWatchedProductsRoute,
  PublicEndedWatchedProductsRoute,
  FeedCustomizationRoute,
  MyFavoritesLandingRoute,
  MyFavoritesWatchListRoute,
  MyFavoritesWatchListEndedRoute,
  MyFavoritesSearchesRoute,
  MyFavoritesShopsRoute,
  MyFavoritesFeedRoute,
  MyFavoritesDiscoverRoute,
  MySavedLandingRoute,
  MySavedWatchListRoute,
  MySavedWatchListEndedRoute,
  MySavedSearchesRoute,
  MySavedShopsRoute,
  MySavedFeedRoute,
  MySavedDiscoverRoute,
  RecentlyViewedRoute,
  WatchedProductsRoute,
  PublicWatchedProductsRoute,
  HolidaySaleRoute,
  CoreIdentityVerificationRoute,
  ...CmsRoutes,
  ...CheckoutRoutes,
  ...CheckoutAnalyticsRoutes,
  ...BrowsePageRoutes,
);
Routes.push(...OnboardingRoutes);

export default Routes;
